import React, { useState, useEffect } from "react";
import {
  Modal,
  Backdrop,
  Fade,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DI from "../../utility/DI";
import { environment } from "../../../Env_data/environment";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "96vw",
  maxWidth: "600px",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
  outline: "none",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];

const getFileExtension = (fileName) =>
  (fileName || "").split(".").pop().toLowerCase();

const isImageFile = (fileName) =>
  imageExtensions.includes(getFileExtension(fileName));

const getFileUrl = (filePath) =>
  environment.BASE_URL.replace("/api/", "/") + filePath;

const BillImageModal = ({
  open,
  onClose,
  expense,
  di: { GET, urls, error },
}) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && expense?.id) {
      fetchImages();
    }
    if (!open) {
      setImages([]);
    }
  }, [open, expense?.id]);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const res = await GET(urls.get.get_bill_images, {
        expense_id: expense.id,
      });
      if (res?.success) {
        setImages(res.images || []);
      } else {
        error(res?.message ?? "Failed to load bill files");
      }
    } catch (err) {
      error("Failed to load bill files");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (img) => {
    const imageUrl = getFileUrl(img.file_path);
    const fileName = img.file_name || `bill_${img.id}`;
    
    try {
      // Try fetching the blob to force a download with the correct name
      const response = await fetch(imageUrl, { mode: 'cors' });
      if (!response.ok) throw new Error("Network response was not ok");
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      console.error("Download fetch failed, falling back to direct link:", err);
      // Fallback: Open in new window/tab as a last resort
      const link = document.createElement("a");
      link.href = imageUrl;
      link.target = "_blank";
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDownloadAll = async () => {
    // Prevent overlapping downloads by adding a small delay
    for (let i = 0; i < images.length; i++) {
      setTimeout(() => {
        handleDownload(images[i]);
      }, i * 500);
    }
  };

  const renderFilePreview = (img, index) => {
    const ext = getFileExtension(img.file_name);

    if (isImageFile(img.file_name)) {
      return (
        <img
          src={getFileUrl(img.file_path)}
          alt={img.file_name}
          style={{
            width: "100%",
            height: "auto",
            display: "block",
            objectFit: "contain",
            maxHeight: "400px",
            backgroundColor: "#f5f5f5",
          }}
          loading="lazy"
        />
      );
    }

    // For PDF/DOC — show a styled placeholder
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "30px 20px",
          backgroundColor: "#f5f5f5",
          minHeight: "120px",
        }}
      >
        {ext === "pdf" ? (
          <PictureAsPdfIcon sx={{ fontSize: 56, color: "#d32f2f" }} />
        ) : (
          <InsertDriveFileIcon sx={{ fontSize: 56, color: "#1565c0" }} />
        )}
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
          {ext.toUpperCase()} Document
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Click download to view
        </Typography>
      </Box>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 400,
          sx: {
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0,0,0,0.6)",
          },
        },
      }}
    >
      <Fade in={open}>
        <Box sx={modalStyle}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 18px",
              borderBottom: "1px solid",
              borderColor: "divider",
              background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
              color: "white",
              borderRadius: "12px 12px 0 0",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <DescriptionIcon />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, lineHeight: 1.2 }}
                >
                  Bill Attachments
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.85,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                  }}
                >
                  {expense?.title} — ₹{expense?.price}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}
            >
              {images.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadAll}
                  sx={{
                    color: "white",
                    borderColor: "rgba(255,255,255,0.5)",
                    textTransform: "none",
                    fontSize: "0.75rem",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      borderColor: "white",
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  All
                </Button>
              )}
              <IconButton onClick={onClose} sx={{ color: "white" }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box
            sx={{
              overflowY: "auto",
              padding: "16px",
              flex: 1,
              maxHeight: "calc(90vh - 80px)",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: "#bdbdbd",
                borderRadius: "3px",
              },
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "200px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : images.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "200px",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <DescriptionIcon
                  sx={{ fontSize: 48, color: "text.disabled" }}
                />
                <Typography color="text.secondary">
                  No bill files found
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              >
                {images.map((img, index) => (
                  <Box
                    key={img.id}
                    sx={{
                      position: "relative",
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "grey.50",
                      transition: "box-shadow 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                      },
                      "&:hover .bill-download-btn": {
                        opacity: 1,
                      },
                    }}
                  >
                    {/* Counter badge */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        background: "rgba(0,0,0,0.6)",
                        color: "white",
                        padding: "2px 10px",
                        borderRadius: "12px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        zIndex: 2,
                      }}
                    >
                      {index + 1} / {images.length}
                    </Box>

                    {/* Download button */}
                    <IconButton
                      className="bill-download-btn"
                      onClick={() => handleDownload(img)}
                      sx={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        background: "rgba(0,0,0,0.55)",
                        color: "white",
                        zIndex: 2,
                        opacity: { xs: 1, md: 0 },
                        transition: "opacity 0.2s ease",
                        "&:hover": { background: "rgba(0,0,0,0.8)" },
                      }}
                      size="small"
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>

                    {/* File preview */}
                    {renderFilePreview(img, index)}

                    {/* File name footer */}
                    <Box
                      sx={{
                        padding: "6px 12px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderTop: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "75%",
                        }}
                      >
                        {img.file_name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.disabled" }}
                      >
                        {new Date(img.uploaded_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default DI(BillImageModal);
